// Places is built before the shell waits for its map. Its DOM stays in
// place, so an open search and early typing survive that wait.
import { el, esc } from '@tol/ui/dom';
import { CONTINENTS, placeSections } from './place-groups.ts';
import type { Region } from './regions.ts';

const isPhone = (): boolean => window.innerWidth <= 680;

// What a Places search matches: a place's name, its path and its old paths,
// without accents or case ("montreal", "koln").
const foldPlace = (s: string): string =>
  s
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/gu, '')
    .toLowerCase();

const words = (r: Region): string => foldPlace([r.name, r.place.slug, ...r.place.aliases].join(' '));

const placeButton = (r: Region): string =>
  `<button type="button" data-region="${esc(r.id)}" data-find="${esc(words(r))}">${esc(r.name)}</button>`;

// The places as buttons under the summary, for keyboards and screen readers
// (the map's dots are neither's: markers.ts), on a phone too. The list starts
// closed: people come for the map. A "Places" chip opens it; picking a
// place, tapping outside the panel or Escape closes it. Open, it is a search box over the places
// by continent and country (place-groups.ts), each country a row: its name,
// then its places. It scrolls within its own height.
export function createPlaces(byId: ReadonlyMap<string, Region>): {
  readonly connect: (go: (region: Region) => void) => boolean;
} {
  let go: ((region: Region) => void) | undefined;
  let pending: Region | undefined;
  const warmed = new Set<string>();
  const warm = (r: Region): void => {
    if (warmed.has(r.id)) return;
    warmed.add(r.id);
    // Only code: installing a runtime or priming JSON would fetch data.
    const code = r.kind === 'gtfs' ? r.code() : import('./london-region.ts');
    void code.catch(() => {
      warmed.delete(r.id);
    });
  };
  const style = document.createElement('style');
  style.textContent = `
#panel.places-starting { z-index: 2001; }
#cities-toggle {
  flex: none; margin-top: 10px; font: 600 13px/1 var(--font); color: var(--text); cursor: pointer;
  background: rgba(255, 255, 255, 0.06); border: 1px solid var(--hair); border-radius: 999px; padding: 5px 10px;
}
#cities-toggle[aria-expanded='true'] { background: rgba(255, 255, 255, 0.14); }
#places { margin: 10px 0 0; }
#places[hidden] { display: none; }
#places-find {
  width: 100%; box-sizing: border-box; font: 14px/1.3 var(--font); color: var(--text);
  background: rgba(255, 255, 255, 0.06); border: 1px solid var(--hair); border-radius: 8px; padding: 7px 10px;
}
#places-find::placeholder { color: var(--muted); }
#places-list { max-height: min(52vh, 460px); overflow-y: auto; margin-top: 8px; padding-right: 4px; }
#places-list h3 {
  margin: 12px 0 4px; font: 600 11px/1.2 var(--font); letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--muted);
}
#places-list h3:first-child { margin-top: 2px; }
#places-list .country {
  display: grid; grid-template-columns: 80px 1fr; column-gap: 6px; align-items: baseline;
  padding: 5px 0; border-top: 1px solid var(--hair);
}
#places-list .country > span { font-size: 12.5px; color: var(--muted); }
#places-list .cities { display: flex; flex-direction: column; gap: 4px; }
#places-list .place-section { display: flex; flex-wrap: wrap; gap: 4px; align-items: baseline; }
#places-list .scope { width: 100%; font-size: 11px; color: var(--muted); }
#places-list .cities button {
  font: 13.5px/1.2 var(--font); color: var(--text); cursor: pointer; background: rgba(255, 255, 255, 0.05);
  border: 1px solid transparent; border-radius: 6px; padding: 3px 7px;
}
#places-list .cities button:hover, #places-list .cities button:focus-visible {
  background: rgba(255, 255, 255, 0.16); border-color: var(--hair); outline: none;
}
#places-list [hidden] { display: none; }
#places-none { margin: 8px 0 0; font-size: 13px; color: var(--muted); }
@media (max-width: 680px) {
  #cities-toggle { grid-column: 1; justify-self: start; margin-top: 6px; }
  #places { grid-column: 1 / -1; }
  #places-list { max-height: 36vh; }
  #places-list .country { grid-template-columns: 80px 1fr; }
}`;
  document.head.append(style);
  const toggle = document.createElement('button');
  toggle.id = 'cities-toggle';
  toggle.type = 'button';
  toggle.setAttribute('aria-controls', 'places');
  const box = document.createElement('div');
  box.id = 'places';
  box.innerHTML =
    `<input id="places-find" type="search" placeholder="Find a place" aria-label="Find a place" autocomplete="off">` +
    `<div id="places-list">${CONTINENTS.map(
      (c) =>
        `<section><h3>${esc(c.name)}</h3>${c.countries
          .map((country) => {
            const groups = placeSections(country)
              .map((group) => {
                const rs = group.codes.flatMap((code) => byId.get(code) ?? []);
                return (
                  '<div class="place-section">' +
                  (group.name === 'Cities' ? '' : '<span class="scope">' + esc(group.name) + '</span>') +
                  rs.map((row) => placeButton(row)).join('') +
                  '</div>'
                );
              })
              .join('');
            return (
              '<div class="country"><span>' +
              esc(country.name) +
              '</span><div class="cities">' +
              groups +
              '</div></div>'
            );
          })
          .join('')}</section>`,
    ).join('')}</div><p id="places-none" hidden>No place by that name yet.</p>`;
  const find = box.querySelector<HTMLInputElement>('#places-find');
  if (!find) throw new Error('no #places-find');
  const listEl = el('#places-list', box);
  const none = el('#places-none', box);
  const visibleButtons = (): HTMLButtonElement[] => {
    const out: HTMLButtonElement[] = [];
    for (const b of listEl.querySelectorAll<HTMLButtonElement>('button[data-region]'))
      if (b.hidden !== true) out.push(b);
    return out;
  };
  const filter = (): void => {
    const q = foldPlace(find.value.trim());
    for (const b of listEl.querySelectorAll<HTMLButtonElement>('button[data-region]')) {
      b.hidden = q !== '' && !(b.dataset['find'] ?? '').includes(q);
    }
    for (const group of listEl.querySelectorAll<HTMLElement>('.place-section')) {
      group.hidden = group.querySelectorAll('button[data-region]:not([hidden])').length === 0;
    }
    for (const row of listEl.querySelectorAll<HTMLElement>('.country')) {
      row.hidden = row.querySelectorAll('button[data-region]:not([hidden])').length === 0;
    }
    for (const sec of listEl.querySelectorAll<HTMLElement>('section')) {
      sec.hidden = sec.querySelectorAll('.country:not([hidden])').length === 0;
    }
    none.hidden = visibleButtons().length > 0;
  };
  const setOpen = (open: boolean): void => {
    box.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? 'Places \u25B4' : 'Places \u25BE';
    if (!open) {
      find.value = '';
      filter();
    }
  };
  const pick = (id: string | undefined): void => {
    const r = id === undefined ? undefined : byId.get(id);
    if (!r) return;
    setOpen(false);
    if (go) go(r);
    else pending = r;
  };
  setOpen(false);
  toggle.addEventListener('click', () => {
    const open = box.hidden === true;
    setOpen(open);
    // A laptop's keyboard goes straight to the search; a phone's would
    // cover the list.
    if (open && !isPhone()) find.focus();
  });
  document.addEventListener(
    'click',
    (e) => {
      if (box.hidden === true || !(e.target instanceof Element) || e.target.closest('#panel') !== null) return;
      setOpen(false);
      if (e.target.closest('#map') !== null) e.stopPropagation();
    },
    true,
  );
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.target instanceof Element && e.target.closest('dialog[open]')) return;
      if (e.key === 'Escape' && box.hidden === false) {
        e.stopPropagation();
        setOpen(false);
        toggle.focus();
      }
    },
    true,
  );
  find.addEventListener('input', filter);
  find.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') pick(visibleButtons()[0]?.dataset['region']);
  });
  listEl.addEventListener('click', (e) => {
    const target = e.target instanceof HTMLElement ? e.target.closest<HTMLElement>('button[data-region]') : null;
    pick(target?.dataset['region']);
  });
  const intent = (e: Event): void => {
    const target = e.target instanceof Element ? e.target.closest<HTMLElement>('button[data-region]') : null;
    const r = byId.get(target?.dataset['region'] ?? '');
    if (r) warm(r);
  };
  listEl.addEventListener('pointerover', (e) => {
    // A phone tap is a selection, not a speculative hover.
    if (e.pointerType !== 'touch') intent(e);
  });
  listEl.addEventListener('focusin', intent);
  // Under the summary: beside the clock it'd squeeze the title.
  el('#updated').after(toggle);
  toggle.after(box);
  return {
    connect: (navigate) => {
      go = navigate;
      if (!pending) return false;
      navigate(pending);
      pending = undefined;
      return true;
    },
  };
}
